import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Building2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { user, login, register } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: "admin@gestloc.fr", password: "Admin1234!", name: "" });

  if (user) return <Navigate to="/" replace />;

  const doLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(form.email, form.password);
      toast.success("Bienvenue !");
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Connexion impossible");
    } finally { setLoading(false); }
  };

  const doRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register({ email: form.email, password: form.password, name: form.name, role: "manager" });
      toast.success("Compte créé !");
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Inscription impossible");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left panel */}
      <div className="hidden lg:flex login-bg relative flex-col justify-between p-12 text-white">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 bg-white rounded-md flex items-center justify-center">
            <Building2 className="w-5 h-5 text-zinc-900" strokeWidth={2.2} />
          </div>
          <div>
            <div className="font-bold text-lg tracking-tight" style={{fontFamily:'Outfit'}}>GestLocative</div>
            <div className="text-[10px] text-zinc-400 uppercase tracking-widest">Gestion locative pro</div>
          </div>
        </div>

        <div className="space-y-4 max-w-md">
          <h1 className="text-5xl font-bold tracking-tight leading-tight" style={{fontFamily:'Outfit'}}>
            La gestion locative,<br/>simplifiée.
          </h1>
          <p className="text-zinc-300 text-base">
            Suivez vos biens, locataires, baux et loyers. Générez vos quittances en un clic. Recevez vos relances automatiquement.
          </p>
        </div>

        <div className="text-xs text-zinc-400 space-y-1">
          <div className="text-overline text-zinc-400">Inclus</div>
          <div>· Tableau de bord temps réel · Quittances PDF auto</div>
          <div>· Assistant IA · Comptabilité · Multi-biens</div>
        </div>
      </div>

      {/* Right panel */}
      <div className="flex items-center justify-center px-6 py-10 bg-white">
        <div className="w-full max-w-sm space-y-6">
          <div>
            <h2 className="text-3xl font-bold tracking-tight" style={{fontFamily:'Outfit'}} data-testid="auth-title">Accès à votre espace</h2>
            <p className="text-sm text-zinc-500 mt-1">Connectez-vous ou créez un compte gestionnaire.</p>
          </div>

          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="login" data-testid="tab-login">Connexion</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">Inscription</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={doLogin} className="space-y-4 mt-4">
                <div className="space-y-1.5">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" data-testid="login-email" type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} required />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="password">Mot de passe</Label>
                  <Input id="password" data-testid="login-password" type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} required />
                </div>
                <Button type="submit" disabled={loading} data-testid="login-submit-btn" className="w-full">
                  {loading ? "Connexion…" : <>Se connecter <ArrowRight className="w-4 h-4 ml-1.5" /></>}
                </Button>
                <div className="text-xs text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-md p-2.5">
                  <strong>Démo</strong> · admin@gestloc.fr / Admin1234!
                </div>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={doRegister} className="space-y-4 mt-4">
                <div className="space-y-1.5">
                  <Label htmlFor="name">Nom complet</Label>
                  <Input id="name" data-testid="register-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="remail">Email</Label>
                  <Input id="remail" data-testid="register-email" type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} required />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="rpassword">Mot de passe</Label>
                  <Input id="rpassword" data-testid="register-password" type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} required minLength={6} />
                </div>
                <Button type="submit" disabled={loading} data-testid="register-submit-btn" className="w-full">
                  {loading ? "Création…" : "Créer mon compte"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
